import * as React from 'react'
import { FC, useState, useEffect, useMemo } from 'react'
import { Modal, ModalBody, ModalHeader } from 'reactstrap'
import { AppointmentData, AssignInvoiceData, ClientPack, InvoiceAssignEntityType, OrderData } from '../../models'
import { useTranslations } from '../../store/translations/hooks'
import { TK } from '../../store/translations/translationKeys'
import { useSelector, useDispatch } from 'react-redux'
import { getEntitiesDataItems } from '../../store/entities/selectors'
import { ClientEntity } from '../../store/entities/state'
import { createClientSearchConfig } from '../../shared/clientSearch'
import { AutocompleteTextField } from './fields'
import AppButton from '../AppButton'
import StyledTable from '../StyledTable'
import {
    getClientAppointmentsAsync,
    getClientPacksAsync,
    getClientOrdersAsync,
    assignInvoiceToEntitiesAsync,
} from '../../ajax/requests'
import { alertError, alertSuccess } from '../../store/alerts/actions'
import { getErrorMessage } from '../../ajax/errors'
import { useGeneralSettings } from '../../store/settings/hooks'
import { serviceName } from '../../shared/appointmint/appointment'
import { formatEuros } from '../../shared/string'
import moment from 'moment'

export interface InvoiceAssignModalProps {
    invoiceId: string
    isOpen: boolean
    onClose: () => void
}

const ENTITY_TYPE_OPTIONS = ['appointment', 'pack', 'order'] as const
type EntityTypeKey = typeof ENTITY_TYPE_OPTIONS[number]

const entityTypeToEnum: Record<EntityTypeKey, InvoiceAssignEntityType> = {
    appointment: InvoiceAssignEntityType.Appointment,
    pack: InvoiceAssignEntityType.Pack,
    order: InvoiceAssignEntityType.Order,
}

export const InvoiceAssignModal: FC<InvoiceAssignModalProps> = ({ invoiceId, isOpen, onClose }) => {
    const t = useTranslations()
    const dispatch = useDispatch()
    const generalSettings = useGeneralSettings()

    const allClients = useSelector(getEntitiesDataItems<ClientEntity>('clients'))
    const clientSearchConfig = useMemo(
        () => createClientSearchConfig(allClients, []),
        [allClients],
    )

    const [entityTypeKey, setEntityTypeKey] = useState<EntityTypeKey | undefined>(undefined)
    const [selectedClientId, setSelectedClientId] = useState<string | undefined>(undefined)
    const [appointments, setAppointments] = useState<AppointmentData[]>([])
    const [packs, setPacks] = useState<ClientPack[]>([])
    const [orders, setOrders] = useState<OrderData[]>([])
    const [selectedEntityIds, setSelectedEntityIds] = useState<string[]>([])
    const [loading, setLoading] = useState(false)
    const [submitting, setSubmitting] = useState(false)

    const entityTypeOptions = [
        t(TK.appointment),
        t(TK.pack),
        t(TK.orders),
    ]

    const entityTypeLabels: Record<EntityTypeKey, string> = {
        appointment: t(TK.appointment),
        pack: t(TK.pack),
        order: t(TK.orders),
    }

    const selectedEntityTypeLabel = entityTypeKey ? entityTypeLabels[entityTypeKey] : ''

    const handleClose = () => {
        setEntityTypeKey(undefined)
        setSelectedClientId(undefined)
        setAppointments([])
        setPacks([])
        setOrders([])
        setSelectedEntityIds([])
        onClose()
    }

    const handleEntityTypeSelected = (label: string) => {
        const key = (Object.entries(entityTypeLabels) as [EntityTypeKey, string][])
            .find(([, v]) => v === label)?.[0]
        setEntityTypeKey(key)
        setSelectedClientId(undefined)
        setAppointments([])
        setPacks([])
        setOrders([])
        setSelectedEntityIds([])
    }

    useEffect(() => {
        if (!selectedClientId || !entityTypeKey) {
            setAppointments([])
            setPacks([])
            setOrders([])
            setSelectedEntityIds([])
            return
        }

        setLoading(true)
        const from = moment().subtract(2, 'months').toDate()
        const to = moment().add(2, 'years').toDate()

        const fetch = entityTypeKey === 'appointment'
            ? getClientAppointmentsAsync(selectedClientId, from, to, 500)
            : entityTypeKey === 'pack'
                ? getClientPacksAsync(selectedClientId, 500)
                : getClientOrdersAsync(selectedClientId, 500)

        fetch
            .then((result) => {
                if (entityTypeKey === 'appointment') setAppointments(result as AppointmentData[])
                else if (entityTypeKey === 'pack') setPacks(result as ClientPack[])
                else setOrders(result as OrderData[])
                setSelectedEntityIds([])
            })
            .catch((error) => {
                const message = getErrorMessage(error)
                dispatch(alertError(message ?? t(TK.somethingWentWrong)))
            })
            .finally(() => setLoading(false))
    }, [selectedClientId, entityTypeKey])

    const handleToggle = (id: string) => {
        setSelectedEntityIds(prev =>
            prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
        )
    }

    const handleToggleAll = (ids: string[]) => {
        if (selectedEntityIds.length === ids.length) {
            setSelectedEntityIds([])
        } else {
            setSelectedEntityIds(ids)
        }
    }

    const handleSubmit = async () => {
        if (!entityTypeKey || selectedEntityIds.length === 0) return

        const data: AssignInvoiceData = {
            entityType: entityTypeToEnum[entityTypeKey],
            entityIds: selectedEntityIds,
        }

        setSubmitting(true)
        try {
            await assignInvoiceToEntitiesAsync(invoiceId, data)
            dispatch(alertSuccess(t(TK.invoiceAssignedSuccessfully)))
            handleClose()
        } catch (error) {
            const message = getErrorMessage(error)
            dispatch(alertError(message ?? t(TK.somethingWentWrong)))
        } finally {
            setSubmitting(false)
        }
    }

    const currentEntityIds = entityTypeKey === 'appointment'
        ? appointments.map(a => a.id)
        : entityTypeKey === 'pack'
            ? packs.map(p => p.id)
            : orders.map(o => o.id)

    const hasEntities = currentEntityIds.length > 0
    const allSelected = hasEntities && selectedEntityIds.length === currentEntityIds.length

    return (
        <Modal isOpen={isOpen} toggle={handleClose} size="lg">
            <ModalHeader toggle={handleClose}>
                {t(TK.assignInvoiceTitle)}
            </ModalHeader>
            <ModalBody>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacer-M)' }}>
                    <AutocompleteTextField
                        label={t(TK.selectEntityType)}
                        value={selectedEntityTypeLabel}
                        options={entityTypeOptions}
                        variant="nobox"
                        onValueChanged={handleEntityTypeSelected}
                    />

                    {entityTypeKey && (
                        <AutocompleteTextField
                            label={t(TK.client)}
                            value={clientSearchConfig.getOptionByClientId(selectedClientId) ?? ''}
                            placeholder={t(TK.client)}
                            options={clientSearchConfig.options}
                            optionLabelResolver={clientSearchConfig.optionLabelResolver}
                            searchTextResolver={clientSearchConfig.searchTextResolver}
                            searchMatcher={clientSearchConfig.searchMatcher}
                            variant="nobox"
                            onValueChanged={(option) => {
                                const client = clientSearchConfig.getClientByOption(option)
                                setSelectedClientId(client?.id ?? undefined)
                            }}
                        />
                    )}

                    {loading && (
                        <div style={{ color: 'var(--text-muted)', fontSize: '0.9rem' }}>
                            {t(TK.loading)}
                        </div>
                    )}

                    {!loading && selectedClientId && entityTypeKey && !hasEntities && (
                        <div style={{ color: 'var(--text-muted)', fontSize: '0.9rem' }}>
                            {t(TK.noEntitiesFound)}
                        </div>
                    )}

                    {!loading && hasEntities && (
                        <div style={{ maxHeight: '18rem', overflowY: 'auto' }}>
                            <StyledTable scrollable hover size="sm">
                                <thead>
                                    <tr>
                                        <th style={{ width: '2rem' }}>
                                            <input
                                                type="checkbox"
                                                checked={allSelected}
                                                onChange={() => handleToggleAll(currentEntityIds)}
                                            />
                                        </th>
                                        {entityTypeKey === 'appointment' && (
                                            <>
                                                <th>{t(TK.date)}</th>
                                                <th>{t(TK.service)}</th>
                                                <th>{t(TK.professional)}</th>
                                            </>
                                        )}
                                        {entityTypeKey === 'pack' && (
                                            <>
                                                <th>{t(TK.pack)}</th>
                                                <th>{t(TK.date)}</th>
                                            </>
                                        )}
                                        {entityTypeKey === 'order' && (
                                            <>
                                                <th>{t(TK.date)}</th>
                                                <th>{t(TK.total)}</th>
                                            </>
                                        )}
                                    </tr>
                                </thead>
                                <tbody>
                                    {entityTypeKey === 'appointment' && appointments.map(a => (
                                        <tr
                                            key={a.id}
                                            style={{ cursor: 'pointer' }}
                                            onClick={() => handleToggle(a.id)}
                                        >
                                            <td>
                                                <input
                                                    type="checkbox"
                                                    checked={selectedEntityIds.includes(a.id)}
                                                    onChange={() => handleToggle(a.id)}
                                                    onClick={e => e.stopPropagation()}
                                                />
                                            </td>
                                            <td>{moment(a.date).format('YYYY-MM-DD HH:mm')}</td>
                                            <td>{serviceName(a.type, generalSettings)}</td>
                                            <td>{a.professionalName ?? ''}</td>
                                        </tr>
                                    ))}
                                    {entityTypeKey === 'pack' && packs.map(p => (
                                        <tr
                                            key={p.id}
                                            style={{ cursor: 'pointer' }}
                                            onClick={() => handleToggle(p.id)}
                                        >
                                            <td>
                                                <input
                                                    type="checkbox"
                                                    checked={selectedEntityIds.includes(p.id)}
                                                    onChange={() => handleToggle(p.id)}
                                                    onClick={e => e.stopPropagation()}
                                                />
                                            </td>
                                            <td>{p.packName}</td>
                                            <td>{p.creationDate ? moment(p.creationDate).format('YYYY-MM-DD') : ''}</td>
                                        </tr>
                                    ))}
                                    {entityTypeKey === 'order' && orders.map(o => (
                                        <tr
                                            key={o.id}
                                            style={{ cursor: 'pointer' }}
                                            onClick={() => handleToggle(o.id)}
                                        >
                                            <td>
                                                <input
                                                    type="checkbox"
                                                    checked={selectedEntityIds.includes(o.id)}
                                                    onChange={() => handleToggle(o.id)}
                                                    onClick={e => e.stopPropagation()}
                                                />
                                            </td>
                                            <td>{moment(o.creationDate).format('YYYY-MM-DD')}</td>
                                            <td>{formatEuros(o.missingToPay ?? 0)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </StyledTable>
                        </div>
                    )}

                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 'var(--spacer-S)' }}>
                        <AppButton onClick={handleClose} disabled={submitting}>
                            {t(TK.cancel)}
                        </AppButton>
                        <AppButton
                            color="success"
                            disabled={selectedEntityIds.length === 0 || submitting}
                            onClick={handleSubmit}
                        >
                            {submitting ? t(TK.loading) : t(TK.assignInvoice)}
                        </AppButton>
                    </div>
                </div>
            </ModalBody>
        </Modal>
    )
}
